import { styled } from '@mui/material/styles';
import { FormControl } from '@mui/material';

// String className constants — used where the className is passed as a prop string
// (Form.formStyle, screenContainer.props.className, SearchBar.containerStyle, titleStyle, etc).
// Selector rules for these live on the AdminContentRoot wrapper component below.
export const ADMIN_FORM_CONTAINER = 'admin-content-form-container';
export const ADMIN_FORM_STYLE = 'admin-content-form-style';
export const ADMIN_FLEX_COLUMN = 'admin-content-flex-column';
export const ADMIN_SUB_SCREEN = 'admin-content-sub-screen';
export const ADMIN_TITLE = 'admin-content-title';
export const ADMIN_SEARCH_BAR = 'admin-content-search-bar';

/**
 * Top-level root wrapper for admin-content pages. Descendant Form/SearchBar receive
 * className strings (above constants) and styles are applied via selectors here.
 *
 * Pass `isMobile` to drive the few mobile-specific tweaks.
 */
export const AdminContentRoot = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  [`& .${ADMIN_FORM_CONTAINER}`]: {
    flexDirection: 'row',
    flexWrap: 'wrap',

    '& .screenWrapper_1': {
      flexDirection: isMobile ? 'column' : 'row',
    },
  },

  [`& .${ADMIN_FORM_STYLE}`]: {
    maxWidth: 'none',

    '& .MuiInput-root.MuiInput-underline.Mui-disabled:before': {
      borderBottom: 'none',
    },
  },

  [`& .${ADMIN_FLEX_COLUMN}`]: {
    flexDirection: 'column',
  },

  [`& .${ADMIN_SUB_SCREEN}`]: {
    width: isMobile ? '100%' : 'calc(50% - 10px)',
  },

  [`& .${ADMIN_TITLE}`]: {
    fontSize: '1rem',
    fontWeight: 'bold',
  },

  [`& .${ADMIN_SEARCH_BAR}`]: {
    marginLeft: 0,
    marginRight: 0,
    marginTop: theme.spacing(2),
  },
}));

export const CreateSc = styled('div')(({ theme }) => ({
  '& .titleContainer': {
    maxWidth: 'none',
  },
  height: '100%',
  padding: theme.spacing(2),
}));

export const AdminContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  position: 'relative',
  padding: isMobile ? 0 : theme.spacing(4),
  maxWidth: 900,
  margin: '0 auto',

  '& .noBorder td': {
    border: 'none',
  },

  '& .offsetCheck': {
    position: 'relative',
    left: 15,
    padding: 0,
  },
}));

export const AdminFlexRow = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  margin: `${theme.spacing(2)} 0`,
}));

export const Pad20 = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ isMobile }) => ({
  padding: isMobile ? 0 : 20,
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  margin: '0 auto',
}));

export const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: 250,
  marginRight: theme.spacing(2),
}));

export const ButtonRow = styled('div')(({ theme }) => ({
  '& button': {
    marginRight: theme.spacing(2),
  },
}));

export const StyledTable = styled('div')(({ theme }) => ({
  width: '100%',

  '& table': {
    width: '100%',
  },
  '& td': {
    fontSize: 12,
  },
  '& tr': {
    cursor: 'pointer',

    '& .parentIcon': {
      transform: 'rotate(90deg)',
      fontSize: 10,
      marginTop: -8,
      marginRight: theme.spacing(0.5),
    },
  },
}));

export const MargBottom = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

export const SmallMarginTopBottom = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

export const IconSpacing = styled('span')(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

export const Manage = styled('div')(({ theme }) => ({
  '& .mainContainer': {
    marginTop: theme.spacing(2),
  },
  '& .buttonRow': {
    flexWrap: 'wrap',
  },
  '& .buttonRow button': {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    maxHeight: 33,
  },
}));

/** Combined AdminContainer + Manage (former `clsx(classes.container, classes.manage)`). */
export const AdminContainerManage = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  position: 'relative',
  // PFM-2599 QA round 2: prod always kept theme.spacing(4) padding (the
  // legacy useStyles prop wasn't threaded, so the isMobile branch never
  // fired). Round 1 mistakenly honored the mobile branch and dropped
  // padding+margin to 0, leaving the edition edit page flush against the
  // viewport edges on small screens. Keep horizontal padding on mobile —
  // smaller than desktop, but never zero — and a consistent top margin.
  padding: isMobile ? theme.spacing(2) : theme.spacing(4),
  marginTop: theme.spacing(2),
  maxWidth: 900,
  marginLeft: 'auto',
  marginRight: 'auto',

  '& .noBorder td': {
    border: 'none',
  },

  '& .offsetCheck': {
    position: 'relative',
    left: 15,
    padding: 0,
  },

  '& .mainContainer': {
    marginTop: theme.spacing(2),
  },
  '& .buttonRow': {
    flexWrap: 'wrap',
  },
  '& .buttonRow button': {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    maxHeight: 33,
  },
}));

/** Combined AdminContainer + flexRow + pad20 (`clsx(classes.container, classes.flexRow, classes.pad20)`). */
export const AdminContainerCentered = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ isMobile }) => ({
  position: 'relative',
  padding: isMobile ? 0 : 20,
  maxWidth: 900,
  margin: '0 auto',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',

  '& .noBorder td': {
    border: 'none',
  },

  '& .offsetCheck': {
    position: 'relative',
    left: 15,
    padding: 0,
  },
}));

/** Combined flexRow + pad20 (`clsx(classes.flexRow, classes.pad20)`). */
export const FlexRowPad20 = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ isMobile }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: isMobile ? 0 : 20,
  margin: '0 auto',
}));

/** Combined flexRow + buttonRow (`clsx(classes.flexRow, classes.buttonRow)`). */
export const FlexRowButtonRow = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  margin: `${theme.spacing(2)} 0`,
  '& button': {
    marginRight: theme.spacing(2),
  },
}));

export const Addedition = styled('div')(({ theme }) => ({
  '& .buttonContainer': {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    height: 60,
    boxShadow: '2px 2px 8px',
    backgroundColor: theme.palette.background.paper,
    zIndex: 100,

    '& .buttonRow': {
      height: 60,
      justifyContent: 'space-around',
    },
  },

  '& .listButtons': {
    marginTop: theme.spacing(2),

    '& button': {
      margin: theme.spacing(1),
    },
  },

  '& .docText': {
    margin: theme.spacing(1),
  },

  '& .buffer': {
    height: 60,
    width: 'calc(100% - 300px)',
    float: 'right',
    [theme.breakpoints.down('lg')]: {
      width: '100%',
      float: 'none',
      position: 'relative',
    },
  },
}));

export const ButtonRowModal = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: 12,
  justifyContent: 'flex-end',
  padding: theme.spacing(1),
  maxWidth: 640,
  width: '100%',
  boxSizing: 'border-box',
  margin: '0 auto',

  '& button': {
    marginLeft: theme.spacing(1),
  },
}));
